
import React, { useCallback, useState, useEffect, useMemo } from 'react';
import {
  ReactFlow,
  addEdge,
  useNodesState,
  useEdgesState,
  Background,
  Controls,
  MiniMap,
  Connection,
  Edge,
  Node,
  ConnectionMode,
  ReactFlowProvider,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import MindMapNode from './MindMapNode';
import MindMapContextMenu from './MindMapContextMenu';
import EdgeContextMenu from './EdgeContextMenu';
import MindMapThemeSelector, { MindMapTheme, themes } from './MindMapThemeSelector';
import { useCompany } from '@/hooks/use-company';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/lib/toast';

const nodeTypes = {
  mindMapNode: MindMapNode,
};

interface MindMapFlowProps {
  boardId: string;
  onSave: (nodes: Node[], edges: Edge[], title: string) => void;
  initialNodes?: Node[];
  initialEdges?: Edge[];
  initialTitle?: string;
}

function MindMapFlowInner({ 
  boardId, 
  onSave, 
  initialNodes = [], 
  initialEdges = [], 
  initialTitle = '' 
}: MindMapFlowProps) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [title, setTitle] = useState(initialTitle);
  const [nodeId, setNodeId] = useState(1);
  const [currentTheme, setCurrentTheme] = useState<MindMapTheme>(themes[0]);
  const { company, updateCompany } = useCompany();
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    visible: boolean;
  }>({ x: 0, y: 0, visible: false });
  const [edgeContextMenu, setEdgeContextMenu] = useState<{
    x: number;
    y: number;
    visible: boolean;
    edgeId?: string;
  }>({ x: 0, y: 0, visible: false });

  const { screenToFlowPosition } = useReactFlow();

  // Load company theme on mount
  useEffect(() => {
    if (company?.mindmap_theme_id) {
      const savedTheme = themes.find(theme => theme.id === company.mindmap_theme_id);
      if (savedTheme) {
        setCurrentTheme(savedTheme);
        console.log('Loaded company theme:', savedTheme.name);
      }
    }
  }, [company]);

  // Auto-save functionality with debounce
  useEffect(() => {
    if (nodes.length > 0 && title.trim()) {
      const timer = setTimeout(() => {
        console.log('Auto-saving mindmap with nodes:', nodes);
        onSave(nodes, edges, title.trim());
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [nodes, edges, title, onSave]);

  const handleNodeUpdate = useCallback((id: string, label: string, color: string, description?: string, imageUrl?: string) => {
    console.log('MindMapFlow - handleNodeUpdate:', { id, label, color, description, imageUrl });
    setNodes((nds) =>
      nds.map((node) =>
        node.id === id
          ? {
              ...node,
              data: { ...node.data, label, color, description, imageUrl },
            }
          : node
      )
    );
  }, [setNodes]);

  const handleNodeDelete = useCallback((id: string) => {
    console.log('MindMapFlow - handleNodeDelete called for node:', id);
    
    setNodes((currentNodes) => {
      const nodeToDelete = currentNodes.find(node => node.id === id);
      if (!nodeToDelete) {
        console.log('Node not found for deletion:', id);
        return currentNodes;
      }
      
      const filteredNodes = currentNodes.filter((node) => node.id !== id);
      console.log('Nodes before deletion:', currentNodes.length);
      console.log('Nodes after deletion:', filteredNodes.length);
      console.log('Deleted node:', nodeToDelete.data.label);
      return filteredNodes;
    });
    
    setEdges((currentEdges) => {
      const edgesBeforeDeletion = currentEdges.length;
      const filteredEdges = currentEdges.filter((edge) => edge.source !== id && edge.target !== id);
      console.log('Edges before deletion:', edgesBeforeDeletion);
      console.log('Edges after deletion:', filteredEdges.length);
      return filteredEdges;
    });
  }, [setNodes, setEdges]);

  const handleEdgeDelete = useCallback((edgeId: string) => {
    console.log('MindMapFlow - handleEdgeDelete called for edge:', edgeId);
    setEdges((currentEdges) => {
      const filteredEdges = currentEdges.filter((edge) => edge.id !== edgeId);
      console.log('Edge deleted:', edgeId);
      return filteredEdges;
    });
  }, [setEdges]);

  const handleAddChild = useCallback((parentId: string) => {
    const parentNode = nodes.find((node) => node.id === parentId);
    if (!parentNode) return;

    const newNodeId = String(nodeId);
    const childPosition = {
      x: parentNode.position.x + 250,
      y: parentNode.position.y + (Math.random() - 0.5) * 200,
    };

    const newNode = {
      id: newNodeId,
      type: 'mindMapNode',
      position: childPosition,
      data: {
        label: 'Nova Ideia',
        color: '#10B981',
        description: '',
        imageUrl: '',
        boardId: boardId,
        onUpdate: handleNodeUpdate,
        onDelete: handleNodeDelete,
        onAddChild: handleAddChild,
        onConvertToCard: handleConvertToCard,
        onEdgeDelete: handleEdgeDelete,
      },
    };

    const newEdge = {
      id: `${parentId}-${newNodeId}`,
      source: parentId,
      target: newNodeId,
      type: 'smoothstep',
      style: { stroke: '#6B7280', strokeWidth: 2 },
    };

    setNodes((nds) => [...nds, newNode]);
    setEdges((eds) => [...eds, newEdge]);
    setNodeId((id) => id + 1);
  }, [nodes, nodeId, boardId, handleNodeUpdate, handleNodeDelete, handleEdgeDelete, setNodes, setEdges]);

  const handleAddIsolatedNode = useCallback((position?: { x: number; y: number }) => {
    const newNodeId = String(nodeId);
    const nodePosition = position || {
      x: 400 + (Math.random() - 0.5) * 400,
      y: 300 + (Math.random() - 0.5) * 300,
    };

    const newNode = {
      id: newNodeId,
      type: 'mindMapNode',
      position: nodePosition,
      data: {
        label: 'Nova Ideia',
        color: '#8B5CF6',
        description: '',
        imageUrl: '',
        boardId: boardId,
        onUpdate: handleNodeUpdate,
        onDelete: handleNodeDelete,
        onAddChild: handleAddChild,
        onConvertToCard: handleConvertToCard,
        onEdgeDelete: handleEdgeDelete,
      },
    };

    setNodes((nds) => [...nds, newNode]);
    setNodeId((id) => id + 1);
  }, [nodeId, boardId, handleNodeUpdate, handleNodeDelete, handleAddChild, handleEdgeDelete, setNodes]);

  const handleConvertToCard = useCallback(async (nodeId: string, columnId: string) => {
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;

    console.log('Converting node to card:', { 
      nodeId, 
      columnId, 
      nodeLabel: node.data.label,
      nodeDescription: node.data.description,
      nodeImageUrl: node.data.imageUrl
    });

    try {
      // Se o nó tem uma imagem, criar anexo
      if (node.data.imageUrl) {
        // Primeiro criar o cartão
        const { data: columnCards } = await supabase
          .from('kanban_cards')
          .select('id')
          .eq('column_id', columnId)
          .eq('is_archived', false);
        
        const position = columnCards ? columnCards.length : 0;
        
        const { data: newCard, error: cardError } = await supabase
          .from('kanban_cards')
          .insert({
            column_id: columnId,
            title: node.data.label,
            description: node.data.description || '',
            position: position
          })
          .select()
          .single();
          
        if (cardError) throw cardError;
        
        // Agora criar o anexo com a imagem do nó
        const { error: attachmentError } = await supabase
          .from('kanban_card_attachments')
          .insert({
            card_id: newCard.id,
            name: 'Imagem do Mapa Mental',
            url: node.data.imageUrl,
            file_type: 'image',
            size: 0 // Não temos o tamanho exato, mas não é crítico
          });
          
        if (attachmentError) {
          console.error('Error creating attachment:', attachmentError);
          // Não vamos falhar a conversão por causa do anexo
        }
      } else {
        // Criar cartão sem anexo
        const { data: columnCards } = await supabase
          .from('kanban_cards')
          .select('id')
          .eq('column_id', columnId)
          .eq('is_archived', false);
        
        const position = columnCards ? columnCards.length : 0;
        
        const { error } = await supabase
          .from('kanban_cards')
          .insert({
            column_id: columnId,
            title: node.data.label,
            description: node.data.description || '',
            position: position
          });
          
        if (error) throw error;
      }
      
      handleNodeDelete(nodeId);
      toast.success('Ideia convertida em cartão com sucesso!');
    } catch (error) {
      console.error('Error converting node to card:', error);
      toast.error('Erro ao converter ideia em cartão');
    }
  }, [nodes, handleNodeDelete]);

  // Edge context menu handlers
  const handleEdgeContextMenu = useCallback((event: React.MouseEvent, edge: Edge) => {
    event.preventDefault();
    event.stopPropagation();
    
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    setEdgeContextMenu({
      x: event.clientX,
      y: event.clientY,
      visible: true,
      edgeId: edge.id,
    });
  }, []);

  const handleEdgeContextMenuDelete = useCallback(() => {
    if (edgeContextMenu.edgeId) {
      console.log('Deleting edge:', edgeContextMenu.edgeId);
      setEdges((currentEdges) => {
        const filteredEdges = currentEdges.filter((edge) => edge.id !== edgeContextMenu.edgeId);
        console.log('Edges before deletion:', currentEdges.length);
        console.log('Edges after deletion:', filteredEdges.length);
        return filteredEdges;
      });
    }
    setEdgeContextMenu(prev => ({ ...prev, visible: false }));
  }, [edgeContextMenu.edgeId, setEdges]);

  const handleCloseEdgeContextMenu = useCallback(() => {
    setEdgeContextMenu(prev => ({ ...prev, visible: false }));
  }, []);

  // Context menu handlers with improved positioning
  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    
    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      visible: true,
    });
  }, []);

  const handleContextMenuAddNode = useCallback((event: React.MouseEvent) => {
    // Converter coordenadas da tela para coordenadas do flow
    const position = screenToFlowPosition({
      x: contextMenu.x,
      y: contextMenu.y,
    });
    
    handleAddIsolatedNode(position);
    setContextMenu(prev => ({ ...prev, visible: false }));
  }, [handleAddIsolatedNode, screenToFlowPosition, contextMenu.x, contextMenu.y]);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(prev => ({ ...prev, visible: false }));
  }, []);

  const handleThemeChange = useCallback(async (theme: MindMapTheme) => {
    setCurrentTheme(theme);
    
    // Save theme to company
    if (company) {
      try {
        await updateCompany({ mindmap_theme_id: theme.id });
        toast.success(`Tema "${theme.name}" salvo para a empresa`);
        console.log('Theme saved to company:', theme.name);
      } catch (error) {
        console.error('Error saving theme:', error);
        toast.error('Erro ao salvar tema');
      }
    }
    
    // Update existing edges with new style
    setEdges((currentEdges) => 
      currentEdges.map(edge => ({
        ...edge,
        type: theme.edgeType,
        style: { 
          stroke: theme.edgeColor, 
          strokeWidth: 2 
        },
      }))
    );
  }, [setEdges, company, updateCompany]);

  useEffect(() => {
    if (initialNodes.length === 0) {
      const rootNode = {
        id: '0',
        type: 'mindMapNode',
        position: { x: 400, y: 300 },
        data: {
          label: 'Ideia Principal',
          color: '#3B82F6',
          description: '',
          imageUrl: '',
          isRoot: true,
          boardId: boardId,
          onUpdate: handleNodeUpdate,
          onDelete: handleNodeDelete,
          onAddChild: handleAddChild,
          onConvertToCard: handleConvertToCard,
          onEdgeDelete: handleEdgeDelete,
        },
      };
      setNodes([rootNode]);
      setNodeId(1);
    } else {
      const nodesWithHandlers = initialNodes.map((node: any) => ({
        ...node,
        data: {
          ...node.data,
          description: node.data.description || '',
          imageUrl: node.data.imageUrl || '',
          boardId: boardId,
          onUpdate: handleNodeUpdate,
          onDelete: handleNodeDelete,
          onAddChild: handleAddChild,
          onConvertToCard: handleConvertToCard,
          onEdgeDelete: handleEdgeDelete,
        },
      }));
      setNodes(nodesWithHandlers);
      setEdges(initialEdges);
      
      const maxId = Math.max(...initialNodes.map(n => parseInt(n.id) || 0), 0);
      setNodeId(maxId + 1);
    }
  }, []);

  const onConnect = useCallback(
    (params: Connection) => {
      const edge = {
        ...params,
        type: currentTheme.edgeType,
        style: { stroke: currentTheme.edgeColor, strokeWidth: 2 },
      };
      setEdges((eds) => addEdge(edge, eds));
    },
    [setEdges, currentTheme]
  );

  const getNodeColor = useCallback((node: Node): string => {
    return (node.data?.color as string) || currentTheme.nodeColors[0];
  }, [currentTheme]);

  const memoizedNodeTypes = useMemo(() => nodeTypes, []);

  const edgesWithContextMenu = useMemo(() => {
    return edges.map(edge => ({
      ...edge,
      style: {
        ...edge.style,
        cursor: 'pointer',
        stroke: currentTheme.edgeColor,
        strokeWidth: 2,
      },
    }));
  }, [edges, currentTheme]);

  return (
    <div className="w-full h-screen flex flex-col">
      <div className="bg-white border-b p-4 flex items-center justify-between">
        <Input
          placeholder="Título do Mapa Mental"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-64"
        />
        
        <div className="flex gap-2">
          <MindMapThemeSelector 
            currentTheme={currentTheme}
            onThemeChange={handleThemeChange}
          />
          <Button
            onClick={() => handleAddIsolatedNode()}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Adicionar Ideia
          </Button>
        </div>
      </div>

      <div 
        className="flex-1 relative" 
        style={{ backgroundColor: currentTheme.backgroundColor }}
        onContextMenu={handleContextMenu} 
        onClick={(e) => {
          handleCloseContextMenu();
          handleCloseEdgeContextMenu();
        }}
      >
        <ReactFlow
          nodes={nodes}
          edges={edgesWithContextMenu}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onEdgeContextMenu={handleEdgeContextMenu}
          nodeTypes={memoizedNodeTypes}
          connectionMode={ConnectionMode.Loose}
          fitView
          attributionPosition="bottom-left"
          selectNodesOnDrag={false}
          panOnDrag={true}
          zoomOnScroll={true}
          zoomOnPinch={true}
          panOnScroll={false}
          preventScrolling={true}
          onPaneClick={() => {
            handleCloseContextMenu();
            handleCloseEdgeContextMenu();
          }}
        >
          <Background 
            color={currentTheme.isDark ? '#374151' : '#e5e7eb'}
            gap={16}
          />
          <Controls />
          <MiniMap
            zoomable
            pannable
            nodeColor={getNodeColor}
            className="bg-white"
          />
        </ReactFlow>

        <MindMapContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          visible={contextMenu.visible}
          onAddNode={handleContextMenuAddNode}
          onClose={handleCloseContextMenu}
        />

        <EdgeContextMenu
          x={edgeContextMenu.x}
          y={edgeContextMenu.y}
          visible={edgeContextMenu.visible}
          onDelete={handleEdgeContextMenuDelete}
          onClose={handleCloseEdgeContextMenu}
        />
      </div>
    </div>
  );
}

export default function MindMapFlow(props: MindMapFlowProps) {
  return (
    <ReactFlowProvider>
      <MindMapFlowInner {...props} />
    </ReactFlowProvider>
  );
}
